import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
  IRefillCardFormModel,
  refillCardFormDefaultValues,
  refillCardFormSchema,
} from "../models/refillCardFormModel";
import { useEffect, useMemo, useRef, useState } from "react";
import { IRefillCardPlanModel } from "../models/refillCardPlanModel";
import { IRefillCardInitialDataModel } from "../models/refillCardInitialDataModel";
import { PaymentTypes, PlanConsumerType } from "../../../../../common/enums";
import { useAppContext } from "../../../../../AppContext";
import { useFormValidationMessages } from "../../../../../common/infrastructure/hooks/useFormHelper";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";

export const useRefillCardForm = (
  isLoading: boolean,
  selectedStudioId?: number,
  studioInstructors?: ISelectFieldModel[],
  paymentOptions?: PaymentTypes[],
  initialData?: IRefillCardInitialDataModel,
  userPlanId?: number
) => {
  const { currentUser } = useAppContext();
  const [availableUsers, setAvailableUsers] = useState<ISelectFieldModel[]>([]);
  const [availableFamilies, setAvailableFamilies] = useState<
    ISelectFieldModel[]
  >([]);

  const form = useForm<IRefillCardFormModel>({
    defaultValues: refillCardFormDefaultValues,
    resolver: yupResolver(refillCardFormSchema),
  });

  useFormValidationMessages<IRefillCardFormModel>(form);

  const selectedPlanId = form.watch("planId");
  const selectedUserId = form.watch("userId");
  const selectedFamilyId = form.watch("familyId");

  // Пазим последния избор на потребител и на семейство, за да не се губи при
  // смяна на план със същия тип консуматор.
  const lastSelectedConsumerIds = useRef<{
    userId: number;
    familyId: number;
  }>({ userId: 0, familyId: 0 });

  useEffect(() => {
    if (selectedUserId) {
      lastSelectedConsumerIds.current.userId = selectedUserId;
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedFamilyId) {
      lastSelectedConsumerIds.current.familyId = selectedFamilyId;
    }
  }, [selectedFamilyId]);

  const currentlySelectedPlan = useMemo(() => {
    return initialData?.plans?.find((x) => x.plan.id === selectedPlanId)?.plan;
  }, [selectedPlanId, initialData]);

  const selectConsumerDropdownFieldsData = (plan?: IRefillCardPlanModel) => {
    if (!initialData || !plan) return;

    const availableConsumers = initialData.plans?.find(
      (p) => p.plan.id === plan.id
    )?.availableConsumers;

    if (plan.consumerType === PlanConsumerType.Family) {
      setAvailableFamilies(availableConsumers ?? []);
    } else if (plan.consumerType === PlanConsumerType.User) {
      setAvailableUsers(availableConsumers ?? []);
    }
  };

  const populateConsumerFieldValue = (plan: IRefillCardPlanModel) => {
    if (!initialData || initialData.currentlySelectedSubscription) return;

    const availableConsumers = initialData.plans?.find(
      (p) => p.plan.id === plan.id
    )?.availableConsumers;

    const isFamilyPlan = plan.consumerType === PlanConsumerType.Family;
    if (!isFamilyPlan && plan.consumerType !== PlanConsumerType.User) return;

    const consumerField = isFamilyPlan ? "familyId" : "userId";
    const otherConsumerField = isFamilyPlan ? "userId" : "familyId";

    // Запазваме вече избрания потребител/семейство, ако той е наличен и
    // при новоизбрания план. Ресетваме към първия само когато не е.
    const previouslySelectedId = lastSelectedConsumerIds.current[consumerField];
    const keepPreviousSelection =
      !!previouslySelectedId &&
      !!availableConsumers?.some((c) => c.id === previouslySelectedId);

    form.setValue(
      consumerField,
      keepPreviousSelection
        ? previouslySelectedId
        : availableConsumers?.[0]?.id ?? 0
    );
    form.setValue(otherConsumerField, 0);
  };

  useEffect(() => {
    if (isLoading || !initialData?.plans) return;

    const sub = initialData.currentlySelectedSubscription;
    // Only use subscription data if userPlanId is provided (edit mode)
    if (sub && userPlanId && userPlanId > 0) {
      // Присвояваме всички стойности от абонацията
      Object.entries(sub).forEach(([key, value]) => {
        if (value !== undefined && key !== "currencyId") {
          form.setValue(key as keyof IRefillCardFormModel, value);
        }
      });
      form.setValue("startDate", new Date(sub.startDate!));
      form.setValue("endDate", new Date(sub.endDate!));

      // Explicitly set currencyId only if it's a valid value (BGN=10 or EUR=20)
      if (sub.currencyId === 10 || sub.currencyId === 20) {
        form.setValue("currencyId", sub.currencyId);
      }

      // Попълваме опции и стойности за dropdown-a при редакция
      const editingPlan = initialData.plans?.find(
        (p) => p.plan.id === sub.planId
      )?.plan;
      if (editingPlan) {
        selectConsumerDropdownFieldsData(editingPlan);
        populateConsumerFieldValue(editingPlan);
      }
    } else {
      // In add mode, explicitly clear editUserPlanId
      form.setValue("editUserPlanId", null);
      if (!!initialData.plans && initialData.plans.length > 0) {
        const firstPlan = initialData.plans[0].plan;

        form.setValue("planId", firstPlan.id);
        selectConsumerDropdownFieldsData(firstPlan);

        form.setValue("price", firstPlan.price);
        form.setValue("numberOfVisits", firstPlan.numberOfVisits);
        form.setValue("endDate", new Date(firstPlan.endDate));
        populateConsumerFieldValue(firstPlan);
      }

      if (paymentOptions?.length) {
        form.setValue("paymentTypeId", paymentOptions[0]);
      }

      if (selectedStudioId) {
        form.setValue("studioId", selectedStudioId);
      }

      const currentInstructor = studioInstructors?.find(
        (x) => x.id === currentUser?.id
      );
      form.setValue(
        "createdById",
        currentInstructor?.id ?? studioInstructors?.[0]?.id ?? 0
      );
    }
  }, [isLoading, initialData, userPlanId]);

  useEffect(() => {
    if (!currentlySelectedPlan) return;

    selectConsumerDropdownFieldsData(currentlySelectedPlan);

    if (!initialData?.currentlySelectedSubscription) {
      form.setValue("price", currentlySelectedPlan.price);
      form.setValue("numberOfVisits", currentlySelectedPlan.numberOfVisits);
      form.setValue("endDate", new Date(currentlySelectedPlan.endDate));
      populateConsumerFieldValue(currentlySelectedPlan);
    }
  }, [currentlySelectedPlan, initialData]);

  return {
    currentlySelectedPlan,
    form,
    availableUsers,
    availableFamilies,
  };
};
